/**
 * Shared helpers for Hotels listing filters (stars, guest-style scores, keyword checks).
 */

const STAR_PROXY_GUEST = { 5: 9.0, 4: 8.2, 3: 7.2, 2: 6.2, 1: 5.5 };

export const REVIEW_SCORE_MIN = {
  wonderful: 9,
  veryGood: 8,
  good: 7,
  pleasant: 6,
};

const GUEST_SCORE_FIELD_KEYS = [
  'guestReviewScore',
  'reviewScore',
  'averageGuestRating',
  'guestRating',
  'avgRating',
  'guestScore',
  'averageRating',
];

/**
 * Star category 1–5 from explicit fields or "4 Star" / category-style strings.
 * Avoids treating long property names as star values.
 */
export function getHotelStarRating(hotel) {
  if (!hotel || typeof hotel !== 'object') return 0;

  for (const key of ['starRating', 'stars', 'starClass']) {
    const v = hotel[key];
    if (typeof v === 'number' && v >= 1 && v <= 5 && Number.isFinite(v)) {
      return Math.round(v);
    }
    if (typeof v === 'string' && v.trim()) {
      const n = parseInt(v, 10);
      if (n >= 1 && n <= 5 && /^\s*\d+\s*$/.test(v)) return n;
    }
  }

  const textSources = [hotel.category, hotel.classification, hotel.rating].filter(
    (x) => x != null && String(x).trim() !== ''
  );

  for (const raw of textSources) {
    const s = String(raw);
    const m = s.match(/(\d+(?:\.\d+)?)\s*\*?\s*(?:star|stars|stelle|sterren)/i);
    if (m) {
      const n = Math.round(parseFloat(m[1]));
      if (n >= 1 && n <= 5) return n;
    }
  }

  for (const raw of textSources) {
    const s = String(raw).trim();
    if (/^\d$/.test(s) || /^\d\.0$/.test(s)) {
      const n = Math.round(parseFloat(s));
      if (n >= 1 && n <= 5) return n;
    }
  }

  return 0;
}

/**
 * Guest / review numeric score (roughly 6–10) when provided explicitly by the API.
 */
export function getHotelExplicitGuestScore(hotel) {
  if (!hotel || typeof hotel !== 'object') return null;

  for (const key of GUEST_SCORE_FIELD_KEYS) {
    const v = hotel[key];
    if (typeof v === 'number' && v > 5 && v <= 10) return v;
    if (typeof v === 'string' && v.trim()) {
      const n = parseFloat(v);
      if (Number.isFinite(n) && n > 5 && n <= 10) return n;
    }
  }

  const r = hotel.rating;
  if (typeof r === 'number' && r > 5 && r <= 10) return r;
  if (r == null || r === '') return null;

  const rs = String(r).trim();
  if (/star|stelle|sterren/i.test(rs)) return null;

  const m = rs.match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const val = parseFloat(m[1]);
  if (!Number.isFinite(val) || val < 5.5 || val > 10) return null;
  return val;
}

/**
 * For filters: use a real guest score when present; otherwise approximate from star class
 * so "Very Good: 8+" still narrows typical catalog data.
 */
export function getHotelGuestScoreForFilter(hotel) {
  const direct = getHotelExplicitGuestScore(hotel);
  if (direct != null) return direct;
  const stars = getHotelStarRating(hotel);
  if (stars >= 1 && stars <= 5) return STAR_PROXY_GUEST[stars] ?? null;
  return null;
}

export function hotelMatchesReviewScoreFilters(hotel, reviewScoreKeys) {
  if (!reviewScoreKeys || reviewScoreKeys.length === 0) return true;
  const score = getHotelGuestScoreForFilter(hotel);
  if (score == null) return false;
  return reviewScoreKeys.some((key) => {
    const min = REVIEW_SCORE_MIN[key];
    return typeof min === 'number' && score >= min;
  });
}

function flattenHotelValues(hotel, depth = 0) {
  if (hotel == null || depth > 4) return '';
  if (typeof hotel === 'string' || typeof hotel === 'number') return ` ${hotel}`;
  if (typeof hotel === 'boolean') return hotel ? ' true' : '';
  if (Array.isArray(hotel)) {
    return hotel.map((x) => flattenHotelValues(x, depth + 1)).join(' ');
  }
  if (typeof hotel === 'object') {
    return Object.values(hotel)
      .map((x) => flattenHotelValues(x, depth + 1))
      .join(' ');
  }
  return '';
}

export function getHotelSearchBlob(hotel) {
  if (!hotel || typeof hotel !== 'object') return '';
  const parts = [
    hotel.name,
    hotel.propertyName,
    hotel.hotelName,
    hotel.description,
    hotel.overview,
    hotel.remarks,
    hotel.city,
    hotel.cityName,
    hotel.address1,
    hotel.address2,
    flattenHotelValues(hotel.facilities),
    flattenHotelValues(hotel.amenities),
    flattenHotelValues(hotel.features),
  ];
  return parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

const FACILITY_KEYWORDS = {
  privateBathroom: ['private bathroom', 'private bath', 'en-suite', 'ensuite', 'en suite'],
  hotTub: ['hot tub', 'jacuzzi', 'whirlpool', 'jetted tub'],
  spa: ['spa ', ' spa', 'wellness', 'sauna', 'hammam'],
  swimmingPool: ['swimming pool', ' outdoor pool', ' indoor pool', 'pool'],
  parking: ['parking', 'car park', 'garage', 'valet parking'],
  fitnessCenter: ['fitness center', 'fitness centre', 'gym', 'workout room', 'health club'],
  kitchen: ['kitchen', 'kitchenette', 'cooking facilities', 'stove', 'microwave'],
  balcony: ['balcony', 'terrace', 'patio'],
  airConditioning: ['air conditioning', 'air-conditioned', ' a/c', ' ac ', 'climate control'],
  privatePool: ['private pool', 'plunge pool'],
};

export function hotelMatchesKeywordFacility(hotel, key) {
  const keywords = FACILITY_KEYWORDS[key];
  if (!keywords) return false;
  const blob = getHotelSearchBlob(hotel);
  if (blob && keywords.some((kw) => blob.includes(kw))) return true;

  const boolMap = {
    swimmingPool: ['pool', 'swimmingPool', 'hasPool'],
    spa: ['spa', 'hasSpa', 'wellness'],
    parking: ['parking', 'hasParking', 'carPark'],
    fitnessCenter: ['gym', 'fitness', 'fitnessCenter'],
    hotTub: ['hotTub', 'jacuzzi', 'whirlpool'],
  };
  const fields = boolMap[key];
  if (fields) {
    return fields.some((f) => hotel[f] === true);
  }
  return false;
}
